import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLanguage } from '../../../contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { WritableSelect } from '../../../components/ui/writable-select';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { api } from '../../../lib/api';
import { toast } from 'sonner';
import type { CarBrand, CarModel, Seller } from '../../../types/car';
import { warehouseCarFormSchema, type WarehouseCarFormData } from '../../../lib/validations';

interface AddCarFormProps {
  onSuccess: () => void;
}

export function AddCarForm({ onSuccess }: AddCarFormProps) {
  const { t } = useLanguage();
  const [brands, setBrands] = useState<CarBrand[]>([]);
  const [models, setModels] = useState<CarModel[]>([]);
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedSeller, setSelectedSeller] = useState('');

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: {},
  } = useForm<WarehouseCarFormData>({
    resolver: zodResolver(warehouseCarFormSchema) as any,
    defaultValues: {
      brandId: 0,
      modelId: 0,
      year: new Date().getFullYear(),
      color: '',
      purchasePrice: 0,
      purchaseCurrency: 'USD' as const,
      sellerId: undefined,
    },
  });

  const currentBrandId = selectedBrand ? brands.find((b) => b.name === selectedBrand)?.id ?? 0 : 0;

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [brandsRes, modelsRes, sellersRes] = await Promise.all([
          api.get('/api/brands'),
          api.get('/api/models'),
          api.get('/api/sellers'),
        ]);
        setBrands(Array.isArray(brandsRes) ? brandsRes : []);
        setModels(Array.isArray(modelsRes) ? modelsRes : []);
        const sellersData = (sellersRes as { sellers?: Seller[] })?.sellers ?? sellersRes;
        setSellers(Array.isArray(sellersData) ? sellersData : []);
      } catch (e) {
        console.error(e);
      }
    };
    fetchAll();
  }, []);

  const createBrand = async (name: string) => {
    const newBrand = await api.post('/api/brands', { name, active: true });
    setBrands((prev) => [...prev, newBrand]);
    setSelectedBrand(newBrand.name);
    setValue('brandId', newBrand.id);
    setSelectedModel('');
    setValue('modelId', 0);
  };

  const createModel = async (name: string) => {
    const newModel = await api.post('/api/models', { name, brandId: currentBrandId, active: true });
    setModels((prev) => [...prev, newModel]);
    setSelectedModel(newModel.name);
    setValue('modelId', newModel.id);
  };

  const handleBrandChange = (name: string) => {
    setSelectedBrand(name);
    const b = brands.find((x) => x.name === name);
    setValue('brandId', b ? b.id : 0);
    setSelectedModel('');
    setValue('modelId', 0);
  };

  const handleModelChange = (name: string) => {
    setSelectedModel(name);
    const m = models.find((x) => x.name === name);
    setValue('modelId', m ? m.id : 0);
  };

  const handleSellerChange = (name: string) => {
    setSelectedSeller(name);
    const s = sellers.find((x) => x.name === name);
    setValue('sellerId', s ? s.id : undefined);
  };

  const onSubmit = async (data: WarehouseCarFormData) => {
    try {
      await api.post('/api/warehouse-cars', {
        brandId: data.brandId,
        modelId: data.modelId,
        year: data.year,
        color: data.color,
        chassisNo: data.chassisNo || undefined,
        plateNumber: data.plateNumber || undefined,
        engineNo: data.engineNo || undefined,
        mileage: data.mileage,
        purchasePrice: data.purchasePrice ?? 0,
        purchaseCurrency: data.purchaseCurrency || 'USD',
        sellerId: data.sellerId || undefined,
        notes: data.notes || undefined,
        registeredOwner: data.registeredOwner || undefined,
      });
      reset({
        brandId: 0,
        modelId: 0,
        year: new Date().getFullYear(),
        color: '',
        purchasePrice: 0,
        purchaseCurrency: 'USD' as const,
        sellerId: undefined,
      });
      setSelectedBrand('');
      setSelectedModel('');
      setSelectedSeller('');
      toast.success(t('warehouse.carAdded'));
      onSuccess();
    } catch (err: unknown) {
      toast.error((err as { error?: string })?.error ?? t('warehouse.carAddError'));
    }
  };

  return (
    <Card className="rounded-2xl border border-slate-200 shadow-sm">
      <CardHeader>
        <CardTitle>{t('warehouse.addCar')}</CardTitle>
        <CardDescription>{t('warehouse.addCarDesc')}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label>{t('sellCar.brand')}</Label>
            <WritableSelect
              value={selectedBrand}
              onValueChange={handleBrandChange}
              options={brands}
              onCreateNew={createBrand}
              placeholder={t('sellCar.brand')}
            />
          </div>
          <div className="space-y-2">
            <Label>{t('sellCar.model')}</Label>
            <WritableSelect
              value={selectedModel}
              onValueChange={handleModelChange}
              options={models.filter((m) => m.brandId === currentBrandId)}
              onCreateNew={createModel}
              placeholder={t('sellCar.model')}
              disabled={!currentBrandId}
            />
          </div>
          <div className="space-y-2">
            <Label>{t('sellCar.year')}</Label>
            <Input type="number" {...register('year', { valueAsNumber: true })} />
          </div>
          <div className="space-y-2">
            <Label>{t('sellCar.color')}</Label>
            <Input {...register('color')} />
          </div>
          <div className="space-y-2">
            <Label>{t('sellCar.chassisNo')}</Label>
            <Input {...register('chassisNo')} />
          </div>
          <div className="space-y-2">
            <Label>{t('sellCar.plateNumber')}</Label>
            <Input {...register('plateNumber')} />
          </div>
          <div className="space-y-2">
            <Label>{t('warehouse.purchasePrice')}</Label>
            <Input type="number" step="any" {...register('purchasePrice', { valueAsNumber: true })} />
          </div>
          <div className="space-y-2">
            <Label>{t('warehouse.currency')}</Label>
            <Select
              value={watch('purchaseCurrency') || 'USD'}
              onValueChange={(v) => setValue('purchaseCurrency', v as 'IQD' | 'USD')}
            >
              <SelectTrigger className="border-slate-200">
                <SelectValue placeholder={t('warehouse.currency')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="IQD">IQD</SelectItem>
                <SelectItem value="USD">USD</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{t('sellCar.selectSeller')}</Label>
            <WritableSelect
              value={selectedSeller}
              onValueChange={handleSellerChange}
              options={sellers}
              placeholder={t('warehouse.sellerPlaceholder')}
            />
          </div>
          <div className="md:col-span-2 flex items-end gap-2">
            <Button
              type="submit"
              className="bg-slate-900 hover:bg-slate-800 text-white rounded-lg px-4 py-2.5 text-sm font-medium disabled:opacity-50 disabled:pointer-events-none"
            >
              {t('warehouse.addToWarehouse')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
